import React from "react";
import moment from "moment";
import { Actions } from "./actions";
import { StatusBadge } from "../base/StatusBadge";
import { Searchbar } from "./searchbar";
import {
  CircularProgress,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import "../list/styles/grid.css";

/**
 * @typedef GridField
 * @property {string} name - Field name
 * @property {"text" | "shortText" | "date" | "number" | "statusMessage"} type - Field type
 * @property {string} header - Column header
 * @property {string} [customClass] - Custom CSS class
 * @property {React.CSSProperties} [customStyle] - Custom inline styles
 */
export type GridField = {
  name: string;
  type: "text" | "shortText" | "date" | "number" | "statusMessage";
  header: string;
  customClass?: string; 
  customStyle?: React.CSSProperties; 
};

// Function to truncate HTML (up to 3 paragraphs)
function truncateHtmlContent(html: string, maxParagraphs: number = 3): string {
  const paragraphRegex = /<p[^>]*>[\s\S]*?<\/p>/gi;
  const paragraphs = html.match(paragraphRegex);
  if (paragraphs && paragraphs.length > maxParagraphs) {
    return paragraphs.slice(0, maxParagraphs).join("") + "<p>...</p>";
  }
  return html;
}

// Function to remove HTML tags
function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, "");
}

/**
 * @param {Object} props - Grid properties
 * @param {string} props.not_found_message - Message to display when no items are found
 * @param {boolean} props.loading_search - Indicates if the search is loading
 * @param {boolean} props.loading_screen - Indicates if the screen is loading
 * @param {string} [props.button_text] - Text for the button
 * @param {string} [props.button_link] - Link for the button
 * @param {React.Component} [props.component] - Custom component
 * @param {boolean} [props.showAuxiliar1] - Indicates if the auxiliar1 button should be displayed
 * @param {boolean} [props.showCorrection] - Indicates if the correction button should be displayed
 * @param {string} [props.top_text] - Text to display at the top of the grid
 * @param {string} props.search_text - Text for the search input
 * @param {any} props.search_input_value - Value of the search input
 * @param {GridField[]} props.fields - Array of grid fields
 * @param {any[]} props.items - Array of items to display in the grid
 * @param {Object} props.pagination - Pagination properties
 * @param {number} props.pagination.last_page - Last page number
 * @param {number} props.pagination.current_page - Current page number
 * @param {Function} [props.onDeleteActionClick] - Function to call when the delete action is clicked
 * @param {Function} [props.onEditActionClick] - Function to call when the edit action is clicked
 * @param {Function} [props.onManagerActionClick] - Function to call when the manager action is clicked
 * @param {Function} [props.onCorrectionActionClick] - Function to call when the correction action is clicked
 * @param {Function} props.onChangePagination - Function to call when the page changes
 * @param {Function} props.onChangeSearchInput - Function to call when the search input changes
 * @param {Function} props.onSearchButtonClick - Function to call when the search button is clicked
 * @param {Function} [props.onAuxiliar1Click] - Function to call when the auxiliar1 button is clicked
 * @param {Function} [props.onClickButton] - Function to call when the button is clicked
 * @param {boolean} [props.hideManager] - Indicates if the manager button should be hidden
 * @param {boolean} [props.hideEdit] - Indicates if the edit button should be hidden
 * @param {boolean} [props.hideDelete] - Indicates if the delete button should be hidden
 * @param {boolean} [props.hideBtn] - Indicates if the button should be hidden
 * @param {boolean} [props.hideActions] - Indicates if the actions column should be hidden
 * @param {Object[]} [props.radioOptions] - Array of radio options
 * @param {string} [props.selectedRadioValue] - Selected radio value
 * @param {Function} [props.onRadioChange] - Function to call when the radio value changes
 * @param {Function} [props.canEditItem] - Function to determine if the item can be edited
 * @returns {JSX.Element} - Grid component
 */
export function Grid(props: {
  not_found_message: string;
  loading_search: boolean;
  loading_screen: boolean;
  button_text?: string;
  button_link?: string;
  component?: any;
  showAuxiliar1?: boolean;
  showCorrection?: boolean; 
  top_text?: string;
  search_text: string;
  search_input_value: any;
  fields: GridField[];
  items: any[];
  pagination: { last_page: number; current_page: number };
  onDeleteActionClick?: (id: any) => void;
  onEditActionClick?: (id: any) => void;
  onManagerActionClick?: (id: any) => void;
  onCorrectionActionClick?: (id: any) => void;
  onChangePagination: (page: number) => void;
  onChangeSearchInput: (value: any) => void;
  onSearchButtonClick: () => void;
  onAuxiliar1Click?: (value: any) => void;
  onClickButton?: any;
  hideManager?: boolean;
  hideEdit?: boolean;
  hideDelete?: boolean;
  hideBtn?: boolean;
  hideActions?: boolean;
  radioOptions?: { label: string; value: string }[];
  selectedRadioValue?: string;
  onRadioChange?: (value: string) => void;
  canEditItem?: (item: any) => boolean;
}) {
  return (
    <div className="row">
      <div className="col-12">
        {props.loading_screen === false && (
          <div>
            {props.top_text && <p className="text-muted">{props.top_text}</p>}
            {props.radioOptions && (
              <div style={{ marginBottom: "1rem" }}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={props.selectedRadioValue || ""}
                    onChange={(event) => {
                      if (props.onRadioChange) {
                        props.onRadioChange(event.target.value);
                      }
                    }}
                  >
                    {props.radioOptions.map((option, index) => (
                      <FormControlLabel
                        key={index}
                        value={option.value}
                        control={<Radio />}
                        label={option.label}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            )}
            <div>
              <Searchbar
                component={props.component}
                search_input_value={props.search_input_value}
                button_text={props.button_text}
                button_link={props.button_link}
                search_text={props.search_text}
                hide_btn={props.hideBtn}
                onClickButton={props.onClickButton}
                onChangeSearchInput={(value: any) => {
                  props.onChangeSearchInput(value);
                }}
                onSearchButtonClick={() => {
                  props.onSearchButtonClick();
                }}
              />
            </div>
            <div>
              <Box
                style={{
                  overflowX: "auto",
                  border: "1px solid #d6d6d6",
                  borderRadius: "0.5rem",
                  marginTop: "1rem",
                }}
              >
                <table
                  className="table table-striped"
                  style={{ minWidth: "600px", borderRadius: "2rem" }}
                >
                  <thead>
                    <tr>
                      {props.fields.map((item, id) => (
                        <th key={id}>{item.header}</th>
                      ))}
                      {!(props.hideActions === true) && (
                        <th data-priority="3">Ações</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {props.loading_search === false &&
                      props.items !== undefined &&
                      props.items.map((item, id) => (
                        <tr key={id}>
                          {props.fields.map((field, field_id) => {
                            const cellClass = field.customClass || "";
                            const cellStyle = field.customStyle || {};
                            if (field.name === "statusMessage") {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={cellStyle}
                                >
                                  <StatusBadge
                                    status={item[field.name] || "Desconhecido"}
                                  />
                                </td>
                              );
                            } else if (field.type === "text") {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={cellStyle}
                                >
                                  <div
                                    style={{
                                      maxHeight: "150px",
                                      overflowY: "auto",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: truncateHtmlContent(
                                        item[field.name]?.toString() ?? "",
                                        3
                                      ),
                                    }}
                                  ></div>
                                </td>
                              );
                            } else if (field.type === "shortText") {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={{
                                    maxWidth: "120px",
                                    height: "40px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    ...cellStyle,
                                  }}
                                >
                                  {stripHtml(
                                    item[field.name]?.toString() ?? ""
                                  )}
                                </td>
                              );
                            } else if (field.type === "date") {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={cellStyle}
                                >
                                  {item[field.name]
                                    ? moment(item[field.name]).format(
                                        "DD/MM/YYYY"
                                      )
                                    : ""}
                                </td>
                              );
                            } else if (field.type === "number") {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={cellStyle}
                                >
                                  {item[field.name] !== undefined &&
                                  item[field.name] !== null
                                    ? Number(item[field.name]).toLocaleString()
                                    : ""}
                                </td>
                              );
                            } else {
                              return (
                                <td
                                  key={field_id}
                                  className={cellClass}
                                  style={cellStyle}
                                >
                                  [undefined type]
                                </td>
                              );
                            }
                          })}
                          {!(props.hideActions === true) && (
                            <td>
                              <Actions
                                onAuxiliar1Click={() => {
                                  if (props.onAuxiliar1Click)
                                    props.onAuxiliar1Click(item._id);
                                }}
                                showAuxiliar1={props.showAuxiliar1}
                                actionItem={item}
                                onEditClick={() => {
                                  if (props.onEditActionClick)
                                    props.onEditActionClick(item._id);
                                }}
                                onDeleteClick={() => {
                                  if (props.onDeleteActionClick)
                                    props.onDeleteActionClick(item._id);
                                }}
                                onManagerClick={() => {
                                  if (props.onManagerActionClick)
                                    props.onManagerActionClick(item._id);
                                }}
                                onCorrectionClick={() => {
                                  if (props.onCorrectionActionClick)
                                    props.onCorrectionActionClick(item._id);
                                }}
                                showCorrection={props.showCorrection}
                                hideEdit={
                                  props.hideEdit ||
                                  (props.canEditItem &&
                                    !props.canEditItem(item))
                                }
                                hideManager={props.hideManager}
                                hideDelete={props.hideDelete}
                              />
                            </td>
                          )}
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Box>
              {props.loading_search && (
                <div className="row mt-3" style={{ margin: 0 }}>
                  <div className="col-12 d-flex justify-content-center align-items-center">
                    <CircularProgress />
                  </div>
                </div>
              )}
              {props.loading_search === false && props.items?.length === 0 && (
                <div className="row w-100 mt-3">
                  <div className="col-12">
                    <p style={{ textAlign: "center" }}>
                      {props.not_found_message}
                    </p>
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: "1rem",
                }}
              >
                <Pagination
                  className={props.pagination.last_page === 1 ? "hide" : ""}
                  count={props.pagination.last_page}
                  page={props.pagination.current_page}
                  onChange={(_, page) => {
                    props.onChangePagination(page);
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {props.loading_screen && (
          <div className="row">
            <div
              className="col-12 d-flex justify-content-center align-items-center"
              style={{ marginTop: "2rem" }}
            >
              <CircularProgress />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
